import { useEffect, useRef } from 'react'

type DirtySource = boolean | (() => boolean)

// Module-level registry. Components register their unsaved state here and the
// two app-wide guards — beforeunload and the router blocker — ask every
// registration at the moment the user tries to leave. Nothing subscribes to
// changes, so typing in a form costs nothing: the answer is only computed on
// the way out.
const sources = new Set<() => boolean>()

/** Imperative form, for callers that aren't components. Returns an unregister. */
export function registerUnsavedChanges(isDirty: () => boolean): () => void {
  sources.add(isDirty)
  return () => {
    sources.delete(isDirty)
  }
}

/** True when any registered component reports unsaved work. */
export function hasUnsavedChanges(): boolean {
  for (const isDirty of sources) {
    if (isDirty()) return true
  }
  return false
}

/**
 * Register this component's unsaved state with the app-wide guards.
 *
 * Pass a boolean the component already computes, or a getter when the answer
 * is expensive enough to be worth deferring to the moment it's asked for.
 * Either way the registration is created once and torn down on unmount — the
 * source is read through a ref, so a re-render never re-subscribes.
 */
export function useUnsavedChanges(source: DirtySource): void {
  const ref = useRef(source)
  ref.current = source

  useEffect(
    () =>
      registerUnsavedChanges(() => {
        const current = ref.current
        return typeof current === 'function' ? current() : current
      }),
    [],
  )
}
